import { BoxShape } from './BoxShape';
import * as THREE from 'three';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';

/**
 * QuaderShape — a textured cuboid extending BoxShape with these differences:
 *  - width   = aspectRatio × 1   (driven by the texture)
 *  - height  = 1
 *  - depth   = poi.poidata.depth.value  (configurable thickness; default 0.15)
 *
 * UV handling and material-group assignment are inherited from BoxShape, so the
 * texture is mapped correctly across all six faces (including aspect preservation
 * for the narrow side faces) and the top/bottom get the solid sides material.
 */
export class QuaderShape extends BoxShape {
    private _lastBuiltAspect: number | null = null;
    private _lastBuiltDepth: number | null = null;

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings);

        // Rebuild on aspect change (skip phantom duplicates).
        this.primaryMaterial.aspectRatio.on((newVal) => {
            if (this._lastBuiltAspect !== null && Math.abs((newVal ?? 1) - this._lastBuiltAspect) < 0.001) return;
            this.recreateGeometry();
        });

        // Rebuild on depth change.
        this.poi.poidata.depth?.on((newVal) => {
            if (this._lastBuiltDepth !== null && Math.abs((newVal ?? 0) - this._lastBuiltDepth) < 0.0005) return;
            this.recreateGeometry();
        });
    }

    protected override createGeometry(): THREE.BufferGeometry {
        const aspect = this.primaryMaterial.aspectRatio.value ?? 1.0;
        const depthSetting = this.poi.poidata.depth?.value;
        const depth = (typeof depthSetting === 'number' && depthSetting > 0.001) ? depthSetting : 0.15;
        const width = 1 * aspect;
        const height = 1;
        this._lastBuiltAspect = aspect;
        this._lastBuiltDepth = depth;
        try { Logger.Instance.Info(`${this.poi.name}: createGeometry w=${width.toFixed(2)} h=${height} d=${depth.toFixed(3)} aspect=${aspect.toFixed(2)}`, 'Quader'); } catch {}
        return new THREE.BoxGeometry(width, height, depth);
    }
}
